import { useEffect, useRef } from 'react'
import { useQuery } from 'react-query'
import ApiClient from '@quiqupltd/quiqupjs/lib/network/api-client'
import { useTracking } from './useTracking'
import { useEnvVars } from './useEnvVars'
import { useAnalytics } from './useAnalytics'
import { useGetShipment } from './useGetShipment'
import theme from '../theme'

export type EligibleItem = {
  sku: string
  description: string
  quantity: number
  sellingPrice?: number
}

export type ReturnReason = {
  code: string
  labelCustomer: string
  labelClient: string
  subReasons: ReturnReason[]
}

// ApiClient camelCases JSON response keys
type ReturnEligibilityResponse = {
  eligible: boolean
  reason?: string
  items?: EligibleItem[]
  returnWindowClosesAt?: string
  returnWindowDays?: number
  reasons?: ReturnReason[]
  existingReturnTrackingUrl?: string
}

const EXISTING_RETURN_REASONS = [
  'return_order_already_exists',
  'return_request_already_exists',
]

export type ReturnWindowUrgency = 'urgent' | 'warning' | 'normal'

const MS_PER_DAY = 1000 * 60 * 60 * 24

export function calculateReturnDaysRemaining(
  returnWindowClosesAt?: string
): number | null {
  if (!returnWindowClosesAt) return null
  const days = Math.ceil(
    (new Date(returnWindowClosesAt).getTime() - Date.now()) / MS_PER_DAY
  )
  return Math.max(0, days)
}

export function getReturnWindowUrgency(
  daysRemaining: number
): ReturnWindowUrgency {
  if (daysRemaining <= 3) return 'urgent'
  if (daysRemaining <= 7) return 'warning'
  return 'normal'
}

export function getReturnWindowUrgencyColor(urgency: ReturnWindowUrgency): string {
  if (urgency === 'urgent') return theme.peach500
  if (urgency === 'warning') return theme.sunflower700
  return theme.slate500
}

export function useReturnEligibility() {
  const { orderDetails } = useTracking()
  const { env } = useEnvVars()
  const { track } = useAnalytics()
  // Salesforce client_id is exposed by the shipments endpoint; we use it as
  // account_id on analytics events for funnel attribution.
  const { data: shipment } = useGetShipment()
  const accountId = shipment?.client_id

  const enabled =
    orderDetails?.state === 'delivery_complete' &&
    orderDetails?.serviceKind !== 'partner_return' &&
    !!orderDetails?.uuid

  const clientOrderId = orderDetails?.id

  const { data, isLoading, isError } = useQuery<ReturnEligibilityResponse>(
    ['return-eligibility', clientOrderId],
    () =>
      ApiClient.get({
        path: `${env.QUIQUP_PLATFORM_URL}/api/return-eligibility/${clientOrderId}`,
        includeAuth: false,
      }),
    { enabled: enabled && !!clientOrderId }
  )

  const hasTrackedRef = useRef(false)
  useEffect(() => {
    if (!data || !clientOrderId || hasTrackedRef.current) return
    hasTrackedRef.current = true

    track('returns.eligibility.checked', {
      action: 'Check',
      category: 'Returns',
      product: 'TrackOrder',
      orderId: String(clientOrderId),
      accountId,
      eligible: data.eligible,
      ineligibilityReason: data.reason,
    })
  }, [data, clientOrderId, accountId, track])

  return {
    eligible: data?.eligible ?? false,
    ineligibilityReason: data?.reason,
    hasExistingReturn: EXISTING_RETURN_REASONS.includes(data?.reason ?? ''),
    existingReturnTrackingUrl: data?.existingReturnTrackingUrl,
    items: data?.items ?? [],
    reasons: data?.reasons ?? [],
    returnWindowClosesAt: data?.returnWindowClosesAt,
    returnWindowDays: data?.returnWindowDays,
    accountId,
    isLoading: enabled && isLoading,
    isError,
  }
}
